import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Package, PackageOpen, Palette } from 'lucide-react';
import {
    ArrowPathIcon,
    Squares2X2Icon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { getThemeContext } from '../providers/ThemeProvider.jsx';
import {
    getFullscreenActivationContext,
    getFullscreenToggleContext,
} from '../providers/FullScreenProvider.jsx';
import TopBar from './TopBar.jsx';
import LoadingScreen from './LoadingScreen.jsx';
import ErrorScreen from './ErrorScreen.jsx';
import InfoPanel from './InfoPanel.jsx';
import ColorPanel from './ColorPanel.jsx';
import ActionButton from './ActionButton.jsx';
import BottomBar from './BottomBar.jsx';

/**
 * Vista común del visor público y del visor de tus modelos (C3D-12): canvas,
 * carga, error, paneles de información y colores y los botones que tienen los
 * dos (grid, reset, abrir la caja, colores, info).
 *
 * Cada página decide solo lo suyo: de dónde sale el modelo (el `viewer` de
 * `useModel3DViewer`), cómo se vuelve atrás, si se pueden tocar los colores y
 * sus acciones propias (`actions`), que van a la derecha en escritorio y junto
 * al reset en móvil. `aside` es lo que se pinta encima del canvas cuando no hay
 * ningún panel abierto, como el banner de captación del visor público.
 */
const ModelViewerShell = ({
    viewer,
    subtitle,
    loadingTitle,
    loadingMessage,
    errorBack,
    back,
    modelName,
    formData,
    canEditColors,
    actions = [],
    aside = null,
    children = null,
}) => {
    const theme = getThemeContext();
    const fullscreenActive = getFullscreenActivationContext();
    const fullscreenToggle = getFullscreenToggleContext();

    const [showInfo, setShowInfo] = useState(false);
    const [showColorPanel, setShowColorPanel] = useState(false);
    const [selectedPartKey, setSelectedPartKey] = useState(null);

    const toggleInfoPanel = useCallback(() => {
        setShowInfo(prev => !prev);
        setShowColorPanel(false);
    }, []);

    const toggleColorPanel = useCallback(() => {
        setShowColorPanel(prev => !prev);
        setShowInfo(false);
    }, []);

    const ready = !viewer.loading && !viewer.error;
    const BackIcon = back.icon;

    // En móvil los botones son solo iconos: el tooltip (hover o pulsación
    // larga, ver ActionButton) dice para qué sirve cada uno.
    const gridTip = viewer.isGridVisible ? 'Ocultar la cuadrícula' : 'Mostrar la cuadrícula';
    const resetTip = 'Volver a encuadrar el modelo';
    const infoTip = 'Ver la información del modelo';

    const actionButtons = (compact) => actions.map(({ key, icon, label, mobileLabel, tip, onClick, isPrimary }) => (
        <ActionButton
            key={key}
            onClick={onClick}
            icon={icon}
            label={compact && mobileLabel ? mobileLabel : label}
            tip={tip || label}
            isPrimary={!compact && isPrimary}
        />
    ));

    // Sólo aparece si el GLB trae piezas separables (`front_*`): la caja de
    // luz. Un llavero es una pieza y no tiene nada que abrir.
    const openButton = (compact) => viewer.canOpen && (
        <ActionButton
            onClick={viewer.toggleOpen}
            icon={viewer.isOpen ? PackageOpen : Package}
            label={compact
                ? (viewer.isOpen ? 'Cerrar' : 'Abrir')
                : (viewer.isOpen ? 'Cerrar la caja' : 'Abrir la caja')}
            tip={viewer.isOpen ? 'Cerrar la caja' : 'Abrir la caja para ver el interior'}
            isActive={viewer.isOpen}
        />
    );

    const colorButton = canEditColors && (
        <ActionButton onClick={toggleColorPanel} icon={Palette} label="Colores" tip="Cambiar los colores de las piezas" isActive={showColorPanel} />
    );

    return (
        <div className="relative w-full h-screen overflow-hidden bg-[color-mix(in_oklch,oklch(var(--b3)),black_12%)]">
            <div ref={viewer.containerRef} className="w-full h-full" />

            {viewer.loading && (
                <LoadingScreen progress={viewer.loadingProgress} title={loadingTitle} message={loadingMessage} />
            )}
            {viewer.error && <ErrorScreen error={viewer.error} onBack={errorBack.onClick} backLabel={errorBack.label} />}

            {viewer.isResetting && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50">
                    <div className="bg-neutral text-neutral-content px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Reseteando vista...</span>
                    </div>
                </div>
            )}

            <TopBar
                theme={theme}
                fullscreenActive={fullscreenActive}
                fullscreenToggle={fullscreenToggle}
                subtitle={subtitle}
            />

            {ready && showInfo && (
                <InfoPanel
                    modelName={modelName}
                    formData={formData}
                    meshCount={viewer.meshCount}
                    onClose={() => setShowInfo(false)}
                />
            )}

            {ready && canEditColors && showColorPanel && (
                <ColorPanel
                    parts={viewer.modelParts}
                    colors={viewer.partsColors}
                    selectedPartKey={selectedPartKey}
                    onSelectPart={setSelectedPartKey}
                    onColorChange={viewer.changePartColor}
                    onClose={() => setShowColorPanel(false)}
                />
            )}

            {ready && !showInfo && !showColorPanel && aside}

            {ready && (
                <BottomBar
                    mobile={<>
                        {/* Los de los extremos abren el tooltip hacia dentro:
                            centrado se saldría de la pantalla. */}
                        <ActionButton onClick={back.onClick} icon={BackIcon} label={back.mobileLabel || back.label} tip={back.tip} tipPosition="right" />
                        <div className="flex items-center space-x-2">
                            <ActionButton onClick={viewer.toggleGrid} icon={Squares2X2Icon} label="Grid" tip={gridTip} isActive={viewer.isGridVisible} />
                            <ActionButton onClick={viewer.resetView} icon={ArrowPathIcon} label="Reset" tip={resetTip} isPrimary />
                            {actionButtons(true)}
                        </div>
                        <div className="flex items-center space-x-2">
                            {openButton(true)}
                            {colorButton}
                            <ActionButton onClick={toggleInfoPanel} icon={InformationCircleIcon} label="Info" tip={infoTip} tipPosition="left" isActive={showInfo} />
                        </div>
                    </>}
                    desktop={<>
                        <div className="flex items-center space-x-2">
                            <ActionButton onClick={back.onClick} icon={BackIcon} label={back.label} tip={back.tip} />
                        </div>
                        <div className="flex items-center space-x-4">
                            <ActionButton
                                onClick={viewer.toggleGrid}
                                icon={Squares2X2Icon}
                                label={viewer.isGridVisible ? 'Ocultar Grid' : 'Mostrar Grid'}
                                tip={gridTip}
                                isActive={viewer.isGridVisible}
                            />
                            <ActionButton onClick={viewer.resetView} icon={ArrowPathIcon} label="Resetear Vista" tip={resetTip} isPrimary />
                            {openButton(false)}
                            {colorButton}
                            <ActionButton onClick={toggleInfoPanel} icon={InformationCircleIcon} label="Información" tip={infoTip} isActive={showInfo} />
                        </div>
                        {actions.length
                            ? <div className="flex items-center space-x-2">{actionButtons(false)}</div>
                            // Spacer para simetría
                            : <div className="w-[4.5rem]" />}
                    </>}
                />
            )}

            {children}
        </div>
    );
};

const actionShape = PropTypes.shape({
    label: PropTypes.string.isRequired,
    mobileLabel: PropTypes.string,
    tip: PropTypes.string,
    icon: PropTypes.elementType.isRequired,
    onClick: PropTypes.func.isRequired,
});

ModelViewerShell.propTypes = {
    viewer: PropTypes.object.isRequired,
    subtitle: PropTypes.string,
    loadingTitle: PropTypes.string,
    loadingMessage: PropTypes.string,
    errorBack: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }).isRequired,
    back: actionShape.isRequired,
    modelName: PropTypes.string,
    formData: PropTypes.object,
    canEditColors: PropTypes.bool,
    actions: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        mobileLabel: PropTypes.string,
        tip: PropTypes.string,
        icon: PropTypes.elementType.isRequired,
        onClick: PropTypes.func.isRequired,
        isPrimary: PropTypes.bool,
    })),
    aside: PropTypes.node,
    children: PropTypes.node,
};

export default ModelViewerShell;
